import { FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useRouter from 'use-react-router';
import Button from 'jbc-front/components/Button';

import { useEmployeeMutation as useMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { Question, QuestionGroup } from '@jbc-year-end-adj/2024/features/questions/types';
import { ButtonContainer } from '@jbc-year-end-adj/2024/features/questions/components/ButtonContainer';
import { HasInsurances as HasInsurancesTemplate } from '@jbc-year-end-adj/2024/features/questions/templates/HasInsurances';
import { findQuestionGroup } from '@jbc-year-end-adj/2024/features/questions/utils';
import { ToggleButton } from '@jbc-year-end-adj/2024/components/form/ToggleButton';
import { Schema, schema, buildDefaultValues } from './schema';
import { ANSWER_HAS_INSURANCES } from './mutation';
import { Profile } from './query';

type HasInsurancesProps = {
  question: Question;
  questionGroups: QuestionGroup[];
  profile: Profile;
};

export const Form: FC<HasInsurancesProps> = ({ question, questionGroups, profile }) => {
  const { history } = useRouter();
  const group = findQuestionGroup(questionGroups, 'check_insurance_and_loan');
  const [answer, { loading }] = useMutation(ANSWER_HAS_INSURANCES);
  const { setValue, watch, handleSubmit } = useForm<Schema>({
    resolver: yupResolver(schema),
    defaultValues: buildDefaultValues(profile, group, question)
  });

  const hasLifeInsurance = watch('hasLifeInsurance');
  const hasEarthquakeInsurance = watch('hasEarthquakeInsurance');
  const hasSocialInsurance = watch('hasSocialInsurance');
  const hasPremium = watch('hasPremium');
  const hasHousingLoan = watch('hasHousingLoan');

  const onSubmit: SubmitHandler<Schema> = async data => {
    await answer({
      variables: {
        input: data
      }
    });
    history.push('/employee_input');
  };

  return (
    <HasInsurancesTemplate>
      <HasInsurancesTemplate.LifeInsurance question={question}>
        <ToggleButton.Container>
          <ToggleButton checked={hasLifeInsurance} onClick={() => setValue('hasLifeInsurance', true)}>
            はい
          </ToggleButton>
          <ToggleButton checked={!hasLifeInsurance} onClick={() => setValue('hasLifeInsurance', false)}>
            いいえ
          </ToggleButton>
        </ToggleButton.Container>
      </HasInsurancesTemplate.LifeInsurance>
      <HasInsurancesTemplate.EarthquakeInsurance question={question}>
        <ToggleButton.Container>
          <ToggleButton checked={hasEarthquakeInsurance} onClick={() => setValue('hasEarthquakeInsurance', true)}>
            はい
          </ToggleButton>
          <ToggleButton checked={!hasEarthquakeInsurance} onClick={() => setValue('hasEarthquakeInsurance', false)}>
            いいえ
          </ToggleButton>
        </ToggleButton.Container>
      </HasInsurancesTemplate.EarthquakeInsurance>
      <HasInsurancesTemplate.SocialInsurance question={question}>
        <ToggleButton.Container>
          <ToggleButton checked={hasSocialInsurance} onClick={() => setValue('hasSocialInsurance', true)}>
            はい
          </ToggleButton>
          <ToggleButton checked={!hasSocialInsurance} onClick={() => setValue('hasSocialInsurance', false)}>
            いいえ
          </ToggleButton>
        </ToggleButton.Container>
      </HasInsurancesTemplate.SocialInsurance>
      <HasInsurancesTemplate.Premium question={question}>
        <ToggleButton.Container>
          <ToggleButton checked={hasPremium} onClick={() => setValue('hasPremium', true)}>
            はい
          </ToggleButton>
          <ToggleButton checked={!hasPremium} onClick={() => setValue('hasPremium', false)}>
            いいえ
          </ToggleButton>
        </ToggleButton.Container>
      </HasInsurancesTemplate.Premium>
      <HasInsurancesTemplate.HousingLoan question={question}>
        <ToggleButton.Container>
          <ToggleButton checked={hasHousingLoan} onClick={() => setValue('hasHousingLoan', true)}>
            はい
          </ToggleButton>
          <ToggleButton checked={!hasHousingLoan} onClick={() => setValue('hasHousingLoan', false)}>
            いいえ
          </ToggleButton>
        </ToggleButton.Container>
      </HasInsurancesTemplate.HousingLoan>

      <ButtonContainer>
        <Button primary onClick={handleSubmit(onSubmit)} disabled={loading}>
          次へ
        </Button>
      </ButtonContainer>
    </HasInsurancesTemplate>
  );
};
